/**
 * בניית הצעה ידנית — TASKS 5.5, PRD 8.3.
 *
 * ⚠️ **חריגה מתועדת מהמפרט:** PRD 8.3 מתאר "טל רואה את האתר בדיוק כמו לקוח —
 *    מצב 'צפה כלקוח'", כלומר עיון בקטלוג הציבורי עצמו. המימוש כאן משתמש
 *    **בבורר המוצרים של הפאנל** (אותו `listProducts`/`getProduct` ששימשו כבר
 *    את כרטיס הלקוח ב-5.4, ראו `CustomerDetailPage.tsx`) ⛔ **ולא** בהטמעת
 *    דפי הקטלוג/מוצר הציבוריים בתוך מעטפת הפאנל. הנימוק: שני ה-UI האלה בנויים
 *    סביב הקשרים שונים לגמרי (Header/Footer/`CartContext` ציבוריים מול ניווט
 *    ואימות של הפאנל), ושכפול מלא שלהם רק כדי להריץ אותם בתוך `/admin` היה
 *    מכפיל קוד בלי לשרת שום דרישה נוספת מעבר לזו שכבר מתקיימת: בחירת מוצר,
 *    מידות, ומחיר לכל שורה — לפני מע"מ, ניתן לדריסה, עם סימון מחיר מיוחד קיים.
 *
 * ⭐ **המחיר תמיד מחושב בשרת** — גם התצוגה המקדימה כאן (`POST /preview`) וגם
 *    ההפקה הסופית (`POST /`) עוברים דרך אותו `computeLinePrice`. הדריסה
 *    הידנית (`priceOverride`) היא פעולה מאושרת של מנהל מחובר, ⛔ לא קלט לקוח
 *    ציבורי — שונה מהותית מהאיסור על מחיר מהלקוח בזרימה הציבורית (PRD 7.4).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminNav } from './AdminNav.js';
import { AdminPageHeader } from './AdminPageHeader.js';
import { isAdminApiError } from './adminApi.js';
import { createCustomer, listCustomers, type CustomerListItem } from './customersApi.js';
import { getProduct, listProducts, type ProductDetail, type Variant } from './productsApi.js';
import {
  createManualQuote,
  previewManualQuote,
  type ManualQuoteMeasurements,
  type PriceBasis,
} from './quotesApi.js';
import { PricingFields, type PricingFieldsResult } from '../product/PricingFields.js';

interface ManualCartLine {
  lineId: string;
  productId: number;
  variantId: number | null;
  sku: string;
  displayName: string;
  measurements: ManualQuoteMeasurements;
  priceOverrideRaw: string;
}

interface PreviewState {
  lines: Array<{
    unitPrice: number;
    lineTotal: number;
    qty: number;
    priceSource: PriceBasis;
    isEstimate: boolean;
    isPriceOnRequest: boolean;
  }>;
  subtotal: number;
  vatRate: number;
  vatAmount: number;
  total: number;
}

const PRICE_SOURCE_NOTE: Record<PriceBasis, string | null> = {
  base: null,
  customer: 'מחיר מיוחד קיים ללקוח זה',
  manual: null,
};

export function ManualQuotePage() {
  const navigate = useNavigate();

  // --- בורר מוצרים ---
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState<
    Array<{ id: number; sku: string; name: string }>
  >([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductDetail | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<Variant[]>([]);
  const [selectedVariantId, setSelectedVariantId] = useState<number | ''>('');
  const [measurementsResult, setMeasurementsResult] = useState<PricingFieldsResult | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);

  // --- עגלת המנהל ---
  const [cart, setCart] = useState<ManualCartLine[]>([]);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // --- לקוח ---
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerLabel, setCustomerLabel] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<CustomerListItem[]>([]);
  const [newCustomerMode, setNewCustomerMode] = useState(false);
  const [newFullName, setNewFullName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [customerError, setCustomerError] = useState<string | null>(null);

  const [saveAsFixedPrices, setSaveAsFixedPrices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function refreshPreview(nextCart: ManualCartLine[], custId: number | null) {
    if (nextCart.length === 0) {
      setPreview(null);
      return;
    }
    try {
      const result = await previewManualQuote(
        custId,
        nextCart.map((line) => ({
          productId: line.productId,
          variantId: line.variantId,
          measurements: line.measurements,
          priceOverride:
            line.priceOverrideRaw.trim() === '' ? undefined : Number(line.priceOverrideRaw),
        })),
      );
      setPreview(result);
      setPreviewError(null);
    } catch (err) {
      setPreviewError(isAdminApiError(err) ? err.message : 'שגיאה בחישוב מחיר.');
    }
  }

  async function handleProductSearch() {
    setPickerError(null);
    try {
      const result = await listProducts({ q: productSearch.trim() || undefined, isActive: true });
      setSearchResults(result.products.map((p) => ({ id: p.id, sku: p.sku, name: p.name })));
    } catch (err) {
      setPickerError(isAdminApiError(err) ? err.message : 'שגיאה בחיפוש מוצרים.');
    }
  }

  async function handleSelectProduct(productId: number) {
    setPickerError(null);
    try {
      const { product, variants } = await getProduct(productId);
      setSelectedProduct(product);
      setSelectedVariants(variants.filter((v) => v.is_active));
      setSelectedVariantId('');
      setMeasurementsResult(null);
      setSearchResults([]);
    } catch (err) {
      setPickerError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת המוצר.');
    }
  }

  function handleAddToCart() {
    if (!selectedProduct || !measurementsResult?.isValid) {
      setPickerError('יש למלא מידות תקינות לפני ההוספה.');
      return;
    }
    const variant = selectedVariants.find((v) => v.id === selectedVariantId) ?? null;
    const newLine: ManualCartLine = {
      lineId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      productId: selectedProduct.id,
      variantId: variant?.id ?? null,
      sku: variant?.sku ?? selectedProduct.sku,
      displayName: variant ? `${selectedProduct.name} ${variant.label}` : selectedProduct.name,
      measurements: measurementsResult.measurements,
      priceOverrideRaw: '',
    };
    const nextCart = [...cart, newLine];
    setCart(nextCart);
    setSelectedProduct(null);
    setSelectedVariants([]);
    setSelectedVariantId('');
    setMeasurementsResult(null);
    setProductSearch('');
    void refreshPreview(nextCart, customerId);
  }

  function handleRemoveLine(lineId: string) {
    const nextCart = cart.filter((line) => line.lineId !== lineId);
    setCart(nextCart);
    void refreshPreview(nextCart, customerId);
  }

  function handleOverrideChange(lineId: string, value: string) {
    setCart((prev) =>
      prev.map((line) => (line.lineId === lineId ? { ...line, priceOverrideRaw: value } : line)),
    );
  }

  async function handleCustomerSearch() {
    setCustomerError(null);
    try {
      const result = await listCustomers({ q: customerSearch.trim() || undefined });
      setCustomerResults(result.customers);
    } catch (err) {
      setCustomerError(isAdminApiError(err) ? err.message : 'שגיאה בחיפוש לקוחות.');
    }
  }

  function selectCustomer(id: number, label: string) {
    setCustomerId(id);
    setCustomerLabel(label);
    setCustomerResults([]);
    setNewCustomerMode(false);
    void refreshPreview(cart, id);
  }

  async function handleCreateCustomer() {
    setCustomerError(null);
    try {
      const { customer } = await createCustomer({
        fullName: newFullName.trim(),
        phone: newPhone.trim(),
        email: newEmail.trim() || undefined,
      });
      selectCustomer(customer.id, `${customer.full_name} (${customer.phone_e164})`);
      setNewFullName('');
      setNewPhone('');
      setNewEmail('');
    } catch (err) {
      setCustomerError(isAdminApiError(err) ? err.message : 'שגיאה ביצירת הלקוח.');
    }
  }

  async function handleSubmit() {
    setSubmitError(null);
    if (!customerId) {
      setSubmitError('יש לבחור או ליצור לקוח לפני ההפקה.');
      return;
    }
    if (cart.length === 0) {
      setSubmitError('העגלה ריקה - יש להוסיף לפחות פריט אחד.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await createManualQuote({
        customerId,
        items: cart.map((line) => ({
          productId: line.productId,
          variantId: line.variantId,
          measurements: line.measurements,
          priceOverride:
            line.priceOverrideRaw.trim() === '' ? undefined : Number(line.priceOverrideRaw),
        })),
        saveAsFixedPrices,
      });
      navigate(`/admin/quotes/${result.quoteId}`, { replace: true });
    } catch (err) {
      setSubmitError(isAdminApiError(err) ? err.message : 'שגיאה בהפקת ההצעה.');
    } finally {
      setSubmitting(false);
    }
  }

  const hasAnyOverride = cart.some((line) => line.priceOverrideRaw.trim() !== '');

  return (
    <>
      <AdminNav />
      <main className="mx-auto max-w-4xl px-6 py-12">
        <AdminPageHeader
          title="הצעה ידנית חדשה"
          description="בניית הצעת מחיר עבור לקוח, כולל אפשרות לדרוס מחיר לכל שורה."
        />

        <section
          className="border-line bg-surface mt-6 rounded-lg border p-6"
          aria-labelledby="picker-h"
        >
          <h2 id="picker-h" className="text-lg font-semibold">
            הוספת מוצר
          </h2>

          <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_auto]">
            <input
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder='חיפוש מוצר (שם או מק"ט)'
              className="bg-canvas rounded-md border border-line px-3 py-2"
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void handleProductSearch()}
            >
              חיפוש
            </button>
          </div>

          {pickerError && (
            <p role="alert" className="text-danger mt-2 text-sm">
              {pickerError}
            </p>
          )}

          {searchResults.length > 0 && (
            <ul className="mt-3 space-y-1">
              {searchResults.map((product) => (
                <li key={product.id}>
                  <button
                    type="button"
                    className="bg-canvas w-full rounded border border-line px-2 py-1 text-start"
                    onClick={() => void handleSelectProduct(product.id)}
                  >
                    <span className="ltr-inline">{product.sku}</span> - {product.name}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selectedProduct && (
            <div className="border-line mt-4 border-t pt-4">
              <p className="text-sm font-semibold">
                <span className="ltr-inline">{selectedProduct.sku}</span> - {selectedProduct.name}
              </p>

              {selectedVariants.length > 0 && (
                <div className="mt-3">
                  <label htmlFor="manual-variant" className="block text-sm font-semibold">
                    ואריאנט
                  </label>
                  <select
                    id="manual-variant"
                    value={selectedVariantId}
                    onChange={(e) =>
                      setSelectedVariantId(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    className="bg-canvas mt-1 w-full max-w-xs rounded-md border border-line px-3 py-2"
                  >
                    <option value="">- בלי ואריאנט -</option>
                    {selectedVariants.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mt-4">
                <PricingFields
                  pricingType={selectedProduct.pricing_type}
                  unitLabel={selectedProduct.unit_label}
                  minOrderQty={selectedProduct.min_order_qty}
                  qtyStep={selectedProduct.qty_step}
                  singleQuantityField={
                    selectedProduct.sku === 'MG-0001' || selectedProduct.sku === 'MG-0002'
                  }
                  singleQuantityFieldLabel={selectedProduct.sku === 'MG-0002' ? 'כמות*' : undefined}
                  onChange={setMeasurementsResult}
                />
              </div>

              <button type="button" className="btn btn-cta mt-4" onClick={handleAddToCart}>
                הוסף לעגלה
              </button>
            </div>
          )}
        </section>

        <section
          className="border-line bg-surface mt-6 rounded-lg border p-6"
          aria-labelledby="cart-h"
        >
          <h2 id="cart-h" className="text-lg font-semibold">
            עגלת ההצעה ({cart.length})
          </h2>
          <p className="text-muted mt-1 text-sm">
            מחירים לפני מע&quot;מ. אפשר לדרוס כל שורה בנפרד.
          </p>

          {previewError && (
            <p role="alert" className="text-danger mt-2 text-sm">
              {previewError}
            </p>
          )}

          {cart.length === 0 && <p className="text-muted mt-3 text-sm">העגלה ריקה.</p>}

          {cart.length > 0 && (
            <ul className="mt-3 space-y-3">
              {cart.map((line, index) => {
                const priced = preview?.lines[index];
                const note = priced ? PRICE_SOURCE_NOTE[priced.priceSource] : null;
                return (
                  <li key={line.lineId} className="border-line rounded border p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span>
                        <span className="ltr-inline font-semibold">{line.sku}</span> -{' '}
                        {line.displayName}
                        {priced && (
                          <span className="text-muted">
                            {' '}
                            · {priced.qty} ·{' '}
                            {priced.isPriceOnRequest
                              ? 'מחיר לפי בקשה - יש להזין מחיר ידני'
                              : `מחיר מחושב: ₪${priced.unitPrice}`}
                          </span>
                        )}
                        {note && line.priceOverrideRaw.trim() === '' && (
                          <span className="text-muted"> ({note})</span>
                        )}
                      </span>
                      <button
                        type="button"
                        className="rounded border border-line px-2 py-1"
                        onClick={() => handleRemoveLine(line.lineId)}
                      >
                        הסרה
                      </button>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <label htmlFor={`override-${line.lineId}`} className="text-sm font-semibold">
                        מחיר קבוע חדש (₪, אופציונלי)
                      </label>
                      <input
                        id={`override-${line.lineId}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.priceOverrideRaw}
                        onChange={(e) => handleOverrideChange(line.lineId, e.target.value)}
                        onBlur={() => void refreshPreview(cart, customerId)}
                        className="tabular ltr-inline w-32 rounded-md border border-line px-2 py-1"
                      />
                      {priced && (
                        <span className="tabular text-sm">
                          סה&quot;כ לשורה: ₪{priced.lineTotal}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {preview && cart.length > 0 && (
            <dl className="mt-4 space-y-1 border-t border-line pt-3 text-sm">
              <div className="flex justify-between">
                <dt>לפני מע&quot;מ</dt>
                <dd className="tabular">₪{preview.subtotal}</dd>
              </div>
              <div className="flex justify-between">
                <dt>מע&quot;מ ({preview.vatRate}%)</dt>
                <dd className="tabular">₪{preview.vatAmount}</dd>
              </div>
              <div className="flex justify-between font-semibold">
                <dt>סה&quot;כ</dt>
                <dd className="tabular">₪{preview.total}</dd>
              </div>
            </dl>
          )}
        </section>

        <section
          className="border-line bg-surface mt-6 rounded-lg border p-6"
          aria-labelledby="customer-h"
        >
          <h2 id="customer-h" className="text-lg font-semibold">
            לקוח
          </h2>

          {customerId ? (
            <p className="mt-2 text-sm">
              נבחר: <span className="font-semibold">{customerLabel}</span>{' '}
              <button
                type="button"
                className="rounded border border-line px-2 py-1"
                onClick={() => {
                  setCustomerId(null);
                  setCustomerLabel('');
                  void refreshPreview(cart, null);
                }}
              >
                שינוי
              </button>
            </p>
          ) : (
            <>
              <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_auto]">
                <input
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder='חיפוש לקוח (שם, טלפון או דוא"ל)'
                  className="bg-canvas rounded-md border border-line px-3 py-2"
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => void handleCustomerSearch()}
                >
                  חיפוש
                </button>
              </div>

              {customerResults.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {customerResults.map((customer) => (
                    <li key={customer.id}>
                      <button
                        type="button"
                        className="bg-canvas w-full rounded border border-line px-2 py-1 text-start"
                        onClick={() =>
                          selectCustomer(
                            customer.id,
                            `${customer.full_name} (${customer.phone_e164})`,
                          )
                        }
                      >
                        {customer.full_name} -{' '}
                        <span className="ltr-inline">{customer.phone_e164}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <button
                type="button"
                className="mt-3 rounded border border-line px-2 py-1 text-sm"
                onClick={() => setNewCustomerMode((v) => !v)}
              >
                {newCustomerMode ? 'ביטול' : 'לקוח חדש'}
              </button>

              {newCustomerMode && (
                <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_1fr_2fr_auto] items-end">
                  <div>
                    <label htmlFor="new-cust-name" className="block text-sm font-semibold">
                      שם מלא
                    </label>
                    <input
                      id="new-cust-name"
                      value={newFullName}
                      onChange={(e) => setNewFullName(e.target.value)}
                      className="mt-1 w-full rounded-md border border-line px-3 py-2"
                    />
                  </div>
                  <div>
                    <label htmlFor="new-cust-phone" className="block text-sm font-semibold">
                      טלפון
                    </label>
                    <input
                      id="new-cust-phone"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      className="ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2"
                    />
                  </div>
                  <div>
                    <label htmlFor="new-cust-email" className="block text-sm font-semibold">
                      דוא&quot;ל (אופציונלי)
                    </label>
                    <input
                      id="new-cust-email"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-cta"
                    onClick={() => void handleCreateCustomer()}
                  >
                    יצירה ובחירה
                  </button>
                </div>
              )}
            </>
          )}

          {customerError && (
            <p role="alert" className="text-danger mt-3 text-sm">
              {customerError}
            </p>
          )}
        </section>

        <section
          className="border-line bg-surface mt-6 rounded-lg border p-6"
          aria-labelledby="submit-h"
        >
          <h2 id="submit-h" className="text-lg font-semibold">
            הפקה ושליחה
          </h2>

          <label className="mt-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={saveAsFixedPrices}
              disabled={!hasAnyOverride}
              onChange={(e) => setSaveAsFixedPrices(e.target.checked)}
            />
            שמור מחירים אלה כמחירי קבע ללקוח זה
          </label>

          {submitError && (
            <p role="alert" className="text-danger mt-3 text-sm">
              {submitError}
            </p>
          )}

          <button
            type="button"
            className="btn btn-cta mt-4"
            disabled={submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting ? 'מפיק…' : 'הפק ושלח'}
          </button>
        </section>
      </main>
    </>
  );
}
